import React, { useCallback } from "react";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { MediaStage } from "@/core/types/mediaTypes";
import { MediaCardContainer, AspectRatio } from "./MediaCardContainer";
import { MediaDisplay } from "./MediaDisplay";
import * as Overlay from "./MediaOverlay";
import * as Badge from "./MediaBadges";
import { StageDropdown } from "@/core/MediaItem/StageDropdown";
import { useImageActions } from "@/core/MediaItem/useImageActions";

interface PostSelectorMediaCardProps {
  mediaItem: MediaItem;
  clientId: string;
  isSelected?: boolean;
  selectionIndex?: number;
  showNumbering?: boolean;
  onClick?: () => void;
  onStageChange?: (updatedItem: MediaItem, oldStage: MediaStage) => void;
  onActionError?: (error: string) => void;
}

export const PostSelectorMediaCard: React.FC<PostSelectorMediaCardProps> = ({
  mediaItem,
  clientId,
  isSelected = false,
  selectionIndex = 0,
  showNumbering = false,
  onClick,
  onStageChange,
  onActionError,
}) => {
  const {
    isChangingStage,
    updateStage,
  } = useImageActions(clientId);

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-interactive]")) {
      return;
    }
    onClick?.();
  };

  const handleStageChange = useCallback(
    async (newStage: string) => {
      // Use the mediaItem prop directly - React Query keeps it fresh
      const oldStage = mediaItem.stage as MediaStage;

      // Guard against same-stage transitions
      if (mediaItem.stage === newStage) {
        console.log(`ℹ️  Media item ${mediaItem.getName()} already at stage ${newStage}`);
        return;
      }

      try {
        const updatedMediaItem = await updateStage(mediaItem, newStage);
        if (updatedMediaItem) {
          onStageChange?.(updatedMediaItem, oldStage);
          console.log(`✅ Media item ${mediaItem.getName()} stage updated to ${newStage}`);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to update stage";
        console.error("❌ Failed to update stage:", err);
        onActionError?.(errorMessage);
      }
    },
    [mediaItem, updateStage, onStageChange, onActionError]
  );

  // Determine aspect ratio based on content type
  const getAspectRatio = (): AspectRatio => {
    if (mediaItem.isVideo()) {
      const contentType = mediaItem.getContentType();
      if (contentType === "stories" || contentType === "reels") {
        return "9:16";
      }
    }
    return "4:5";
  };

  return (
    <MediaCardContainer
      aspectRatio={getAspectRatio()}
      selected={isSelected}
      onClick={handleCardClick}
    >
      <MediaDisplay
        mediaItem={mediaItem}
        useThumbnail={false}
        showPlayButton={true}
      />

      {/* Top-left: Stage dropdown */}
      <Overlay.TopLeft showOnHover interactive>
        <StageDropdown
          mediaItem={mediaItem}
          onStageChange={handleStageChange}
          isChanging={isChangingStage}
          disabled={false}
        />
        {mediaItem.isProcessingFailed() && (
          <Badge.ProcessingBadge status="failed" />
        )}
      </Overlay.TopLeft>

      {/* Bottom-left: Selection and usage */}
      <Overlay.BottomLeft>
        {isSelected && (
          <Badge.SelectionBadge
            index={selectionIndex}
            showNumber={showNumbering}
          />
        )}
        {mediaItem.usageCount > 0 && (
          <Badge.UsageBadge count={mediaItem.usageCount} />
        )}
      </Overlay.BottomLeft>

      {/* Bottom-right: Group indicator */}
      {mediaItem.group && (
        <Overlay.BottomRight>
          <Badge.GroupBadge group={mediaItem.group} />
        </Overlay.BottomRight>
      )}
    </MediaCardContainer>
  );
};
